import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Save, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditMemberModal = ({ userId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/customers/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const u = res.data.user;
        const p = res.data.profile || {};
        setForm({
          // user-doc fields
          name: u.name || '',
          email: u.email || '',
          phone: u.phone || '',
          is_active: u.is_active !== false,
          newsletter_subscribed: !!u.newsletter_subscribed,
          admin_notes: u.admin_notes || '',
          // profile fields
          date_of_birth: p.date_of_birth || '',
          experience_types: (p.experience_types || []).join(', '),
          hotel_budget: p.hotel_budget || '',
          planning_scope: (p.planning_scope || []).join(', '),
          top_priorities: (p.top_priorities || []).join(', '),
          past_trips_loved: p.past_trips_loved || '',
          favorite_hotels: p.favorite_hotels || '',
          dietary_requirements: p.dietary_requirements || '',
          allergies: p.allergies || '',
          emergency_contact_name: p.emergency_contact_name || '',
          emergency_contact_phone: p.emergency_contact_phone || '',
          emergency_contact_relationship: p.emergency_contact_relationship || '',
        });
      } catch {
        toast.error('Failed to load member');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, onClose]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...form,
        experience_types: form.experience_types.split(',').map((s) => s.trim()).filter(Boolean),
        planning_scope: form.planning_scope.split(',').map((s) => s.trim()).filter(Boolean),
        top_priorities: form.top_priorities.split(',').map((s) => s.trim()).filter(Boolean),
      };
      const res = await axios.put(`${API}/admin/customers/${userId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="edit-member-modal">
      <div className="bg-cream rounded-sm border-2 border-maroon shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-maroon/10 sticky top-0 bg-cream z-10">
          <h2 className="text-2xl font-heading font-bold text-maroon">Edit Member</h2>
          <button onClick={onClose} className="text-maroon/50 hover:text-maroon p-1" aria-label="Close" data-testid="edit-member-close-btn">
            <X size={22} />
          </button>
        </div>

        {loading || !form ? (
          <p className="p-8 text-maroon/60">Loading…</p>
        ) : (
          <div className="p-5 space-y-5">
            {/* Identity */}
            <section className="space-y-3">
              <h3 className="font-heading font-bold text-lg text-maroon">Identity</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input value={form.name} onChange={(e) => set('name', e.target.value)} className="input-field" data-testid="edit-member-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="input-field" data-testid="edit-member-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} className="input-field" data-testid="edit-member-phone" />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input type="date" value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} className="input-field" data-testid="edit-member-dob" />
                </div>
              </div>
            </section>

            {/* Status & subscription */}
            <section className="space-y-3">
              <h3 className="font-heading font-bold text-lg text-maroon">Status</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 cursor-pointer p-3 border-2 border-maroon/20 rounded-sm bg-white">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} data-testid="edit-member-active" />
                  <span className="text-sm font-semibold text-maroon">Active member</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-3 border-2 border-maroon/20 rounded-sm bg-white">
                  <input type="checkbox" checked={form.newsletter_subscribed} onChange={(e) => set('newsletter_subscribed', e.target.checked)} data-testid="edit-member-newsletter" />
                  <span className="text-sm font-semibold text-maroon">Subscribed to newsletter</span>
                </label>
              </div>
            </section>

            {/* Admin notes */}
            <section className="space-y-2">
              <h3 className="font-heading font-bold text-lg text-maroon">Customer Service Notes <span className="text-xs font-normal text-coral">— admin-only, never shown to the customer</span></h3>
              <Textarea
                value={form.admin_notes}
                onChange={(e) => set('admin_notes', e.target.value)}
                rows={4}
                placeholder="VIP, dietary considerations not in profile, things the customer mentioned on a call, etc."
                className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
                data-testid="edit-member-admin-notes"
              />
            </section>

            {/* Traveler profile (collapsible) */}
            <section className="border-2 border-maroon/15 rounded-sm">
              <button
                type="button"
                onClick={() => setShowProfile((v) => !v)}
                className="w-full flex items-center justify-between p-4 text-left text-maroon font-heading font-bold text-lg hover:bg-sand/30"
                data-testid="edit-member-toggle-profile"
              >
                <span>Traveler Profile (preferences & emergency contact)</span>
                {showProfile ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              {showProfile && (
                <div className="p-4 border-t border-maroon/10 space-y-3 bg-sand/20">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Hotel budget</Label>
                      <Input value={form.hotel_budget} onChange={(e) => set('hotel_budget', e.target.value)} placeholder="$200-400" className="input-field" />
                    </div>
                    <div>
                      <Label>Experience types (comma-separated)</Label>
                      <Input value={form.experience_types} onChange={(e) => set('experience_types', e.target.value)} placeholder="Beach, Cultural, Adventure" className="input-field" />
                    </div>
                    <div>
                      <Label>Planning scope (comma-separated)</Label>
                      <Input value={form.planning_scope} onChange={(e) => set('planning_scope', e.target.value)} placeholder="Hotels, Flights" className="input-field" />
                    </div>
                    <div>
                      <Label>Top priorities (comma-separated, max 3)</Label>
                      <Input value={form.top_priorities} onChange={(e) => set('top_priorities', e.target.value)} placeholder="Sightseeing, Local Cuisine" className="input-field" />
                    </div>
                  </div>
                  <div>
                    <Label>Past trips loved</Label>
                    <Textarea value={form.past_trips_loved} onChange={(e) => set('past_trips_loved', e.target.value)} rows={2} className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none" />
                  </div>
                  <div>
                    <Label>Favorite hotels</Label>
                    <Textarea value={form.favorite_hotels} onChange={(e) => set('favorite_hotels', e.target.value)} rows={2} className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Dietary requirements</Label>
                      <Input value={form.dietary_requirements} onChange={(e) => set('dietary_requirements', e.target.value)} className="input-field" />
                    </div>
                    <div>
                      <Label>Allergies</Label>
                      <Input value={form.allergies} onChange={(e) => set('allergies', e.target.value)} className="input-field" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Emergency contact name</Label>
                      <Input value={form.emergency_contact_name} onChange={(e) => set('emergency_contact_name', e.target.value)} className="input-field" />
                    </div>
                    <div>
                      <Label>Emergency contact phone</Label>
                      <Input value={form.emergency_contact_phone} onChange={(e) => set('emergency_contact_phone', e.target.value)} className="input-field" />
                    </div>
                    <div>
                      <Label>Relationship</Label>
                      <Input value={form.emergency_contact_relationship} onChange={(e) => set('emergency_contact_relationship', e.target.value)} className="input-field" />
                    </div>
                  </div>
                </div>
              )}
            </section>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-maroon/10">
              <Button variant="outline" onClick={onClose} className="border-maroon/30 text-maroon hover:bg-maroon/5">Cancel</Button>
              <Button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="edit-member-save-btn">
                <Save size={16} /> {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditMemberModal;
